import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanGroupedProduct, FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { PlaceholderProduct } from "@domain/project/floorplan/placeholder-product";
import { ProjectEventType } from "@domain/project/project-event";
import { IsArrayOfInstancesOf } from "@utils/class-validator/class-validator-constraints";
import { Exclude, Expose, Transform, Type } from "class-transformer";
import { IsNumber, IsUUID, ValidateNested } from "class-validator";

export abstract class FloorplanProductItem<T extends ProductConfiguration> extends FloorplanItem {
  @IsNumber()
  @Expose({ name: "size" })
  private _size: number;

  @Expose({ name: "configId" })
  @Transform(
    (params) => {
      if (params.value) {
        return params.value;
      }
      return (
        params.obj.alarmDeviceId ||
        params.obj.transmitterId ||
        params.obj.gasWarningCenterId ||
        params.obj.signalElementId ||
        params.obj.plasticSignId ||
        params.obj.utilityId
      );
    },
    { toClassOnly: true },
  )
  @IsUUID()
  private readonly _configId: string;

  @ValidateNested({ each: true })
  @IsArrayOfInstancesOf(PlaceholderProduct)
  @Type(() => PlaceholderProduct)
  @Expose({ name: "products" })
  @Transform((params) => params.value || [], { toClassOnly: true })
  private _products: PlaceholderProduct[];

  @Exclude()
  private _config: T;

  protected constructor(floorplan: Floorplan, config: T, id: string, x: number, y: number, size: number, notes: string = "") {
    super(floorplan, id, x, y, notes);
    this._size = size;
    this._configId = config?.id; // optional because class transformer initializes without arguments once at beginning
    this._config = config;
    this._products = [];
  }

  override init(floorplan: Floorplan) {
    super.init(floorplan);
    if (this._config) {
      return;
    }
    const config = this.getConfigFromProject(floorplan);
    if (!config) {
      console.warn(
        `Config with id '${this._configId}' is not contained in project anymore. Orphaned floorplan item '${this.id}' will be removed.`,
      );
      return;
    }
    this._config = config;
  }

  protected abstract getConfigFromProject(floorplan: Floorplan): T | undefined;

  get size(): number {
    return this._size;
  }

  get config(): T {
    return this._config;
  }

  get products(): PlaceholderProduct[] {
    return this._products;
  }

  set products(products: PlaceholderProduct[]) {
    this._products = products;
    this._floorplan.publishUpdate(FloorplanEventType.ITEM_UPDATED, this);
    this._floorplan.project.publishUpdate(ProjectEventType.PLACEHOLDER_PRODUCTS_UPDATED, this);
  }

  updateSize(value: number, x: number, y: number) {
    this._size = value;
    this.updatePosition(x, y);
    this._floorplan.publishUpdate(FloorplanEventType.ITEM_SIZE_CHANGED, this);
  }

  override isGroupedProduct(): this is FloorplanGroupedProduct {
    return true;
  }

  protected override copySpecificDuplicateStateTo(): ((duplicate: FloorplanItem) => void) | undefined {
    const products = this.products.map((product) => product.copy());
    const copySpecificDuplicateStateTo = super.copySpecificDuplicateStateTo();

    return (duplicate: FloorplanItem) => {
      const duplicateProductItem = duplicate as FloorplanProductItem<T>;
      if (products.length > 0) {
        duplicateProductItem._products = products.map((product) => product.copy());
      }
      copySpecificDuplicateStateTo?.(duplicateProductItem);
    };
  }

  protected override afterDuplicateAdded(): ((duplicate: FloorplanItem) => void) | undefined {
    return this.afterGroupedDuplicateAdded(super.afterDuplicateAdded());
  }
}
